import { useEffect, useState, useMemo } from 'react';
import { Card, CardHeader, CardContent } from '../../../components/Card';
import { Button } from '../../../components/Button';
import { Badge } from '../../../components/Badge';
import { Key, Lock, Globe, Server, Sliders, Copy, Smartphone, Laptop, Network, Save, RotateCcw, Eye, EyeOff } from 'lucide-react';
import { useAdminStore, SsoConfig, SecurityPolicy } from '../../../store/adminStore';
import { AdminPageHeader } from '../components';
import toast from 'react-hot-toast';

interface PlatformAccessTabProps {
  initialSubTab?: 'sso' | 'mfa' | 'session' | 'network' | 'access' | 'audit';
}

export function PlatformAccessTab({ initialSubTab = 'sso' }: PlatformAccessTabProps) {
  const { 
    ssoConfig, securityPolicy, 
    loadSsoConfig, updateSsoConfig, 
    loadSecurityPolicy, updateSecurityPolicy, 
    isLoading 
  } = useAdminStore();

  const resolveTab = (tab?: string): 'sso' | 'mfa' | 'session' | 'network' => {
    if (tab === 'mfa') return 'mfa';
    if (tab === 'session') return 'session';
    if (tab === 'network' || tab === 'audit') return 'network';
    return 'sso';
  };

  const [activeTab, setActiveTab] = useState<'sso' | 'mfa' | 'session' | 'network'>(resolveTab(initialSubTab));
  const [isSaving, setIsSaving] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [showSecret, setShowSecret] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(new Date());

  // Local form state for SSO
  const [ssoForm, setSsoForm] = useState<Partial<SsoConfig>>({
    provider: 'trustybytes',
    status: 'enforced',
    issuer: 'https://auth.trustybytes.com',
    clientId: 'teamone-platform-enterprise',
    metadataUrl: 'https://auth.trustybytes.com/.well-known/openid-configuration',
    callbackUrl: 'https://app.teamone.io/api/auth/sso-login',
    allowedDomains: ['trustybytes.com', 'teamone.io'],
    autoProvision: true,
    defaultRole: 'employee',
    signingAlgorithm: 'RS256',
    enforceForAdmins: true,
    jitEnabled: true,
    certFingerprint: 'SHA256:7B:8A:2E:19:D4:F3:C8:6E:9B:41:5A:02:88:EC:33:F9',
  });

  // Local form state for Security Policy
  const [policyForm, setPolicyForm] = useState<Partial<SecurityPolicy>>({
    mfaEnforcement: 'admins_only',
    allowedMfaMethods: ['totp', 'webauthn', 'email'],
    sessionIdleTimeoutMins: 480,
    maxConcurrentSessions: 3,
    lockoutThreshold: 5,
    lockoutDurationMins: 15,
    minPasswordLength: 10,
    requireSpecialChar: true,
    requireNumbers: true,
    requireUppercase: true,
    passwordMaxAgeDays: 90,
    ipAllowlist: ['127.0.0.1/32', '187.127.179.67/32'],
    blockTorAndProxies: true,
  });

  useEffect(() => {
    loadSsoConfig();
    loadSecurityPolicy();
  }, [loadSsoConfig, loadSecurityPolicy]);

  useEffect(() => {
    if (ssoConfig) {
      setSsoForm(ssoConfig);
    }
  }, [ssoConfig]);

  useEffect(() => {
    if (securityPolicy) {
      setPolicyForm(securityPolicy);
    }
  }, [securityPolicy]);

  // Dirty detection
  const isSsoDirty = useMemo(() => {
    if (!ssoConfig) return false;
    return JSON.stringify(ssoForm) !== JSON.stringify(ssoConfig);
  }, [ssoForm, ssoConfig]);

  const isPolicyDirty = useMemo(() => {
    if (!securityPolicy) return false;
    return JSON.stringify(policyForm) !== JSON.stringify(securityPolicy);
  }, [policyForm, securityPolicy]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([loadSsoConfig(), loadSecurityPolicy()]);
      setLastRefreshed(new Date());
      toast.success('Security settings refreshed');
    } catch {
      toast.error('Failed to refresh security settings');
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleCopy = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    toast.success(`Copied ${field} to clipboard`);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleSaveSso = async () => {
    setIsSaving(true);
    try {
      await updateSsoConfig(ssoForm);
      toast.success('SSO configuration saved');
    } catch (err: any) {
      toast.error(err.message || 'Failed to save SSO configuration');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDiscardSso = () => {
    if (ssoConfig) setSsoForm(ssoConfig);
    toast.success('Changes discarded');
  };

  const handleSavePolicy = async () => {
    setIsSaving(true);
    try {
      await updateSecurityPolicy(policyForm);
      toast.success('Security policies saved');
    } catch (err: any) {
      toast.error(err.message || 'Failed to save security policies');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDiscardPolicy = () => {
    if (securityPolicy) setPolicyForm(securityPolicy);
    toast.success('Changes discarded');
  };

  return (
    <div className="min-h-full bg-background text-foreground p-4 space-y-4 pb-24">
      {/* Page Header */}
      <AdminPageHeader
        icon={Lock}
        title="Service Access & Security"
        subtitle="Enterprise Single Sign-On (SSO), Identity Providers, MFA, session security and network controls"
        onRefresh={handleRefresh}
        isRefreshing={isRefreshing}
        lastRefreshed={lastRefreshed}
      />

      <div className="space-y-4">
        {/* Navigation Sub-Tabs */}
        <div className="flex items-center gap-2 border-b border-border pb-px text-xs">
          <button
            type="button"
            onClick={() => setActiveTab('sso')}
            className={`flex items-center gap-1.5 px-4 py-2 border-b-2 font-medium transition-colors cursor-pointer ${
              activeTab === 'sso'
                ? 'border-primary text-primary font-semibold'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <Globe className="size-3.5" />
            <span>Single Sign-On (SSO)</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('mfa')}
            className={`flex items-center gap-1.5 px-4 py-2 border-b-2 font-medium transition-colors cursor-pointer ${
              activeTab === 'mfa'
                ? 'border-primary text-primary font-semibold'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <Smartphone className="size-3.5" />
            <span>Multi-Factor Authentication (MFA)</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('session')}
            className={`flex items-center gap-1.5 px-4 py-2 border-b-2 font-medium transition-colors cursor-pointer ${
              activeTab === 'session'
                ? 'border-primary text-primary font-semibold'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <Sliders className="size-3.5" />
            <span>Session &amp; Password Policies</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('network')}
            className={`flex items-center gap-1.5 px-4 py-2 border-b-2 font-medium transition-colors cursor-pointer ${
              activeTab === 'network'
                ? 'border-primary text-primary font-semibold'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <Network className="size-3.5" />
            <span>Network &amp; IP Protection</span>
          </button>
        </div>

        {/* 1. SSO TAB */}
        {activeTab === 'sso' && (
          <div className="space-y-6">
            {/* Providers Card Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                {
                  id: 'teamone_sso',
                  name: 'TeamOne Central SSO',
                  type: 'Primary IdP',
                  desc: 'Universal identity gateway across platform services',
                  icon: Server,
                  isConfigured: ssoForm.provider === 'teamone_sso' || ssoForm.provider === 'trustybytes',
                  status: ssoForm.status === 'enforced' ? 'Enforced' : 'Active',
                },
                {
                  id: 'google',
                  name: 'Google Workspace',
                  type: 'OpenID Connect',
                  desc: 'Sign in with verified company Google accounts',
                  icon: Globe,
                  isConfigured: ssoForm.provider === 'google',
                  status: ssoForm.provider === 'google' ? 'Configured' : 'Not configured',
                },
                {
                  id: 'azure_ad',
                  name: 'Microsoft Entra ID',
                  type: 'SAML 2.0 / OIDC',
                  desc: 'Azure Active Directory enterprise federation',
                  icon: Laptop,
                  isConfigured: ssoForm.provider === 'azure_ad',
                  status: ssoForm.provider === 'azure_ad' ? 'Configured' : 'Not configured',
                },
                {
                  id: 'okta',
                  name: 'Okta Identity Cloud',
                  type: 'SAML 2.0',
                  desc: 'Enterprise directory connector & lifecycle push',
                  icon: Key,
                  isConfigured: ssoForm.provider === 'okta',
                  status: ssoForm.provider === 'okta' ? 'Configured' : 'Not configured',
                },
              ].map((idp) => {
                const IconComponent = idp.icon;
                return (
                  <Card
                    key={idp.id}
                    onClick={() => setSsoForm({ ...ssoForm, provider: idp.id })}
                    className={`bg-card border transition-all cursor-pointer ${
                      ssoForm.provider === idp.id
                        ? 'border-primary ring-1 ring-primary/40 shadow-sm'
                        : 'border-border hover:border-border/80'
                    }`}
                  >
                    <CardHeader className="p-4 pb-2 flex flex-row items-center justify-between space-y-0">
                      <div className="flex items-center gap-2.5">
                        <div className="size-9 rounded-lg bg-primary/10 border border-primary/20 text-primary flex items-center justify-center shrink-0">
                          <IconComponent className="size-4.5" />
                        </div>
                        <div>
                          <h3 className="font-display text-card-title text-foreground">{idp.name}</h3>
                          <p className="text-[10px] text-muted-foreground uppercase">{idp.type}</p>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="p-4 pt-2 space-y-2.5">
                      <p className="text-xs text-muted-foreground line-clamp-2">{idp.desc}</p>
                      <div className="flex items-center justify-between pt-1 text-xs">
                        <span className="text-[11px] text-muted-foreground">Status:</span>
                        <Badge
                          variant="outline"
                          className={`text-[10px] ${
                            idp.status === 'Enforced' || idp.status === 'Active' || idp.status === 'Configured'
                              ? 'bg-success/10 text-success border-success/30 font-medium'
                              : 'bg-secondary text-muted-foreground border-border'
                          }`}
                        >
                          {idp.status}
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Grouped SSO Form Sections */}
            <Card className="bg-card border-border">
              <CardHeader className="p-5 border-b border-border">
                <div>
                  <h2 className="font-display text-section-title text-foreground">
                    Federation &amp; Token Rules
                  </h2>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Configure endpoint issuer, client identification, and assertion algorithms
                  </p>
                </div>
              </CardHeader>
              <CardContent className="p-5 space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Issuer URL */}
                  <div>
                    <label className="block text-xs font-semibold text-foreground mb-1">
                      Identity Provider Issuer URL
                    </label>
                    <input
                      type="text"
                      value={ssoForm.issuer || ''}
                      onChange={(e) => setSsoForm({ ...ssoForm, issuer: e.target.value })}
                      className="w-full h-9 rounded-lg border border-border bg-card px-3 text-xs text-foreground font-mono focus:border-primary outline-none"
                    />
                  </div>

                  {/* Client ID */}
                  <div>
                    <label className="block text-xs font-semibold text-foreground mb-1">
                      OAuth / SAML Client ID
                    </label>
                    <input
                      type="text"
                      value={ssoForm.clientId || ''}
                      onChange={(e) => setSsoForm({ ...ssoForm, clientId: e.target.value })}
                      className="w-full h-9 rounded-lg border border-border bg-card px-3 text-xs text-foreground font-mono focus:border-primary outline-none"
                    />
                  </div>

                  {/* Callback URL */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-xs font-semibold text-foreground">Callback URL</label>
                      <button
                        type="button"
                        onClick={() => handleCopy(ssoForm.callbackUrl || '', 'Callback URL')}
                        className="text-[11px] text-primary hover:underline flex items-center gap-1"
                      >
                        <Copy className="size-3" />
                        <span>{copiedField === 'Callback URL' ? 'Copied' : 'Copy'}</span>
                      </button>
                    </div>
                    <input
                      type="text"
                      readOnly
                      value={ssoForm.callbackUrl || ''}
                      className="w-full h-9 rounded-lg border border-border bg-secondary/30 px-3 text-xs text-muted-foreground font-mono outline-none"
                    />
                  </div>

                  {/* Secret / Certificate Fingerprint (Masked) */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-xs font-semibold text-foreground">
                        Certificate Fingerprint / Secret
                      </label>
                      <button
                        type="button"
                        onClick={() => setShowSecret(!showSecret)}
                        className="text-[11px] text-muted-foreground hover:text-foreground flex items-center gap-1 cursor-pointer"
                      >
                        {showSecret ? <EyeOff className="size-3" /> : <Eye className="size-3" />}
                        <span>{showSecret ? 'Mask' : 'Reveal'}</span>
                      </button>
                    </div>
                    <input
                      type={showSecret ? 'text' : 'password'}
                      value={ssoForm.certFingerprint || 'SHA256:7B:8A:2E:19:D4:F3:C8:6E:9B:41:5A:02:88:EC:33:F9'}
                      onChange={(e) => setSsoForm({ ...ssoForm, certFingerprint: e.target.value })}
                      className="w-full h-9 rounded-lg border border-border bg-card px-3 text-xs text-foreground font-mono focus:border-primary outline-none"
                    />
                  </div>
                </div>

                {/* Provisioning & Enforce Toggles */}
                <div className="pt-4 border-t border-border grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <label className="flex items-center gap-2.5 text-xs text-foreground cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={ssoForm.autoProvision ?? true}
                      onChange={(e) => setSsoForm({ ...ssoForm, autoProvision: e.target.checked })}
                      className="rounded border-border text-primary focus:ring-primary cursor-pointer"
                    />
                    <span>Automatic User Provisioning</span>
                  </label>

                  <label className="flex items-center gap-2.5 text-xs text-foreground cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={ssoForm.jitEnabled ?? true}
                      onChange={(e) => setSsoForm({ ...ssoForm, jitEnabled: e.target.checked })}
                      className="rounded border-border text-primary focus:ring-primary cursor-pointer"
                    />
                    <span>JIT (Just-In-Time) Sync</span>
                  </label>

                  <label className="flex items-center gap-2.5 text-xs text-foreground cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={ssoForm.enforceForAdmins ?? true}
                      onChange={(e) => setSsoForm({ ...ssoForm, enforceForAdmins: e.target.checked })}
                      className="rounded border-border text-primary focus:ring-primary cursor-pointer"
                    />
                    <span>Enforce SSO for Administrators</span>
                  </label>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* 2. MFA TAB */}
        {activeTab === 'mfa' && (
          <Card className="bg-card border-border">
            <CardHeader className="p-5 border-b border-border">
              <h2 className="font-display text-section-title text-foreground">
                Multi-Factor Authentication Policies
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                Enforce hardware security keys (FIDO2/WebAuthn), TOTP authenticators, or email codes
              </p>
            </CardHeader>
            <CardContent className="p-5 space-y-5">
              <div>
                <label className="block text-xs font-semibold text-foreground mb-2">
                  Enforcement Scope
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {[
                    { id: 'admins_only', label: 'Admins Only', desc: 'Required for Level 50+ roles' },
                    { id: 'all', label: 'All Users', desc: 'Strict mandatory MFA for everyone' },
                    { id: 'optional', label: 'Optional', desc: 'User self-enrollment permitted' },
                  ].map((opt) => (
                    <div
                      key={opt.id}
                      onClick={() => setPolicyForm({ ...policyForm, mfaEnforcement: opt.id as any })}
                      className={`p-3.5 rounded-lg border cursor-pointer transition-all ${
                        policyForm.mfaEnforcement === opt.id
                          ? 'border-primary bg-primary/5 ring-1 ring-primary/30'
                          : 'border-border hover:bg-secondary/40'
                      }`}
                    >
                      <span className="font-semibold text-xs text-foreground block">{opt.label}</span>
                      <span className="text-[11px] text-muted-foreground">{opt.desc}</span>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* 3. SESSION & PASSWORD POLICIES TAB */}
        {activeTab === 'session' && (
          <Card className="bg-card border-border">
            <CardHeader className="p-5 border-b border-border">
              <h2 className="font-display text-section-title text-foreground">
                Session Lifecycle &amp; Credential Rules
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                Maximum concurrent logins, idle timeouts, and password complexity controls
              </p>
            </CardHeader>
            <CardContent className="p-5 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-foreground mb-1">
                    Idle Timeout (Minutes)
                  </label>
                  <input
                    type="number"
                    value={policyForm.sessionIdleTimeoutMins ?? 480}
                    onChange={(e) => setPolicyForm({ ...policyForm, sessionIdleTimeoutMins: Number(e.target.value) })}
                    className="w-full h-9 rounded-lg border border-border bg-card px-3 text-xs text-foreground focus:border-primary outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-foreground mb-1">
                    Max Concurrent Sessions
                  </label>
                  <input
                    type="number"
                    value={policyForm.maxConcurrentSessions ?? 3}
                    onChange={(e) => setPolicyForm({ ...policyForm, maxConcurrentSessions: Number(e.target.value) })}
                    className="w-full h-9 rounded-lg border border-border bg-card px-3 text-xs text-foreground focus:border-primary outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-foreground mb-1">
                    Lockout Threshold (Attempts)
                  </label>
                  <input
                    type="number"
                    value={policyForm.lockoutThreshold ?? 5}
                    onChange={(e) => setPolicyForm({ ...policyForm, lockoutThreshold: Number(e.target.value) })}
                    className="w-full h-9 rounded-lg border border-border bg-card px-3 text-xs text-foreground focus:border-primary outline-none"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* 4. NETWORK & IP TAB */}
        {activeTab === 'network' && (
          <Card className="bg-card border-border">
            <CardHeader className="p-5 border-b border-border">
              <h2 className="font-display text-section-title text-foreground">
                Network &amp; Perimeter Protection
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                IP allowlisting, VPN enforcement, and automated proxy blocking
              </p>
            </CardHeader>
            <CardContent className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-foreground mb-1">
                  Allowed CIDR Ranges (One per line)
                </label>
                <textarea
                  rows={4}
                  value={(policyForm.ipAllowlist || []).join('\n')}
                  onChange={(e) => setPolicyForm({ ...policyForm, ipAllowlist: e.target.value.split('\n').filter(Boolean) })}
                  className="w-full rounded-lg border border-border bg-card p-3 text-xs text-foreground font-mono focus:border-primary outline-none resize-none"
                />
              </div>
              <label className="flex items-center gap-2.5 text-xs text-foreground cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={policyForm.blockTorAndProxies ?? true}
                  onChange={(e) => setPolicyForm({ ...policyForm, blockTorAndProxies: e.target.checked })}
                  className="rounded border-border text-primary focus:ring-primary cursor-pointer"
                />
                <span>Block Tor exit nodes and anonymous proxies</span>
              </label>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Sticky Save / Discard Bar when Form is Dirty */}
      {(isSsoDirty || isPolicyDirty) && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-card border-t border-primary/40 shadow-2xl p-3 px-6 animate-in slide-in-from-bottom-2">
          <div className="w-full flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <span className="size-2 rounded-full bg-warning animate-pulse" />
              <span className="text-xs font-semibold text-foreground">
                You have unsaved changes in {isSsoDirty ? 'SSO Configuration' : 'Security Policies'}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={isSsoDirty ? handleDiscardSso : handleDiscardPolicy}
                disabled={isSaving}
                className="gap-1 text-xs"
              >
                <RotateCcw className="size-3" />
                <span>Discard Changes</span>
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={isSsoDirty ? handleSaveSso : handleSavePolicy}
                disabled={isSaving}
                className="gap-1.5 text-xs"
              >
                <Save className="size-3.5" />
                <span>{isSaving ? 'Saving...' : 'Save Configuration'}</span>
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
